import { useLingui } from "@lingui/react/macro";
import { useFormContext } from "react-hook-form";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import type { IActivityCategory } from "@medoma/models/activities";
import { categorySchema, orderedCategories } from "@medoma/models/activities";
import { activityCategoryDictionary } from "@/api/Activities";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import type { IAddActivityFormData } from "./AddActivityForm";
import styles from "./ActivityCategoryPicker.module.scss";

export type IActivityCategoryPickerFields = {
  category: IActivityCategory;
};

type IActivityCategoryPicker = {
  categoryConditionals: {
    currentCategoryRequiresCompetence: boolean;
    currentCategoryMayRequireDoubleStaffing: boolean;
  };
  excludedTypes?: IActivityCategory[];
  patientId: string | undefined;
};

export const ActivityCategoryPicker = ({
  categoryConditionals,
  excludedTypes = [],
  patientId,
}: IActivityCategoryPicker) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
    resetField,
  } = useFormContext<IAddActivityFormData>();

  const { data: phoneCallActivityTypeEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.PhoneCallActivityType,
  );

  const filteredCategories = orderedCategories.filter((category) => {
    if (excludedTypes.includes(category)) {
      return false;
    }
    if (!patientId) {
      return category === categorySchema.enum.AdminTask;
    }
    if (
      category === categorySchema.enum.PhoneCall &&
      !phoneCallActivityTypeEnabled
    ) {
      return false;
    }
    return true;
  });

  if (filteredCategories.length === 0) {
    return (
      <ErrorMessage
        message={t`Formuläret är felkonfigurerat, kontakta support för att åtgärda detta`}
      />
    );
  }

  const CategoryRadioButton = (category: IActivityCategory) => (
    <div className={styles.categoryRadioButton}>
      <CategoryIcon category={category} size="small" />
      {t(activityCategoryDictionary[category])}
    </div>
  );

  return (
    <RadioButtons
      legend={t`Typ av aktivitet`}
      errorMessage={errors.category?.message}
      orientation="horizontal"
    >
      {filteredCategories.map((category) => (
        <RadioButton
          key={category}
          label={{
            text: t(activityCategoryDictionary[category]),
            component: CategoryRadioButton(category),
          }}
          visualStyle="framed"
          value={category}
          {...register("category", {
            required: {
              value: true,
              message: t`Välj en kategori för aktiviteten`,
            },
            onChange: () => {
              if (!categoryConditionals.currentCategoryRequiresCompetence) {
                resetField("requiredCompetences");
              }
              if (
                !categoryConditionals.currentCategoryMayRequireDoubleStaffing
              ) {
                resetField("doubleStaffing");
              }
            },
          })}
        />
      ))}
    </RadioButtons>
  );
};
